/**
 * App layout: top nav (h-14) + sidebar (w-60) + main, per react-brand skill's
 * dashboard layout. The active Instance is shown as a badge in the header.
 */

import { useState, type ReactNode } from "react";
import { Navigate, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  LayoutDashboard,
  Server,
  Users,
  Building2,
  FolderOpen,
  FileText,
  Database,
  KeyRound,
  ShieldCheck,
  Search,
  MessageSquare,
  BarChart3,
  LogOut,
  ChevronDown,
  Sun,
  Moon,
  Monitor,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Logo } from "@/components/brand/logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/store/auth";
import { useUi, applyTheme } from "@/store/ui";
import { isSuperAdmin } from "@/lib/rbac";
import { canManageTenant, roleLabel, tenantBasePath } from "@/lib/tenant-workspace";
import { tenantApi, type Tenant } from "@/lib/proxy-api";

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
}

const adminNav: NavItem[] = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard },
  { to: "/instances", label: "Instances", icon: Server, superAdminOnly: true } as NavItem & { superAdminOnly?: boolean },
  { to: "/users", label: "Users", icon: Users, superAdminOnly: true } as NavItem & { superAdminOnly?: boolean },
  { to: "/tenants", label: "Tenants", icon: Building2 },
  { to: "/audit", label: "Audit Log", icon: ShieldCheck },
] as (NavItem & { superAdminOnly?: boolean })[];

function tenantNav(basePath: string, canManage: boolean): NavItem[] {
  return [
    { to: basePath, label: "Tenant Overview", icon: Building2 },
    { to: `${basePath}/collections`, label: "Collections", icon: FolderOpen },
    { to: `${basePath}/documents`, label: "Documents", icon: FileText },
    { to: `${basePath}/knowledge-base`, label: "Knowledge Base", icon: Database },
    ...(canManage ? [{ to: `${basePath}/api-keys`, label: "API Keys", icon: KeyRound }] : []),
  ];
}

function interactionNav(basePath: string): NavItem[] {
  return [
  { to: "/playground/search", label: "Search & RAG", icon: Search },
  { to: "/playground/chat", label: "Chat", icon: MessageSquare },
  { to: "/analytics", label: "Analytics", icon: BarChart3 },
  ].map((item) => ({ ...item, to: `${basePath}${item.to}` }));
}

function NavSection({ title, items }: { title: string; items: (NavItem & { superAdminOnly?: boolean })[] }) {
  const sa = isSuperAdmin();
  const visible = items.filter((i) => !i.superAdminOnly || sa);
  if (visible.length === 0) return null;
  return (
    <div className="space-y-1">
      <p className="px-3 pb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {title}
      </p>
      {visible.map((item) => (
        <NavLink key={item.to} to={item.to} end={item.to === "/"}>
          {({ isActive }) => (
            <Button
              variant="ghost"
              className={cn(
                "justify-start w-full h-8",
                isActive && "bg-primary/10 text-primary font-medium",
              )}
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </Button>
          )}
        </NavLink>
      ))}
    </div>
  );
}

function InstanceBadge() {
  const instance = useAuth((s) => s.instance);
  const role = useAuth((s) => s.user?.role);
  const selectInstance = useAuth((s) => s.selectInstance);
  const [selecting, setSelecting] = useState(false);
  const [name, setName] = useState("");
  const [err, setErr] = useState<string | null>(null);

  // Bootstrap mode: SuperAdmin logged in without an instance. Show a prompt to
  // either create one (Instances page) or select an existing one by name.
  if (!instance) {
    if (role !== "super_admin") return null; // shouldn't happen — TA always has instance
    return (
      <div className="flex items-center gap-2">
        <Badge variant="outline" className="gap-1.5 font-normal border-amber-300 text-amber-700">
          <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
          no instance
        </Badge>
        {selecting ? (
          <form
            className="flex items-center gap-1"
              onSubmit={async (e: React.FormEvent) => {
                e.preventDefault();
                setErr(null);
                try {
                  await selectInstance(name);
                  setSelecting(false);
                  setName("");
                } catch (e2: unknown) {
                  setErr(e2 instanceof Error ? e2.message : "Failed");
                }
              }}
          >
            <Input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Instance name to select"
              className="h-7 w-44 text-xs"
            />
            <Button type="submit" size="sm" className="h-7">Select</Button>
            <Button type="button" size="sm" variant="ghost" className="h-7" onClick={() => setSelecting(false)}>Cancel</Button>
            {err && <span className="text-xs text-destructive">{err}</span>}
          </form>
        ) : (
          <Button size="sm" variant="outline" className="h-7" onClick={() => setSelecting(true)}>
            Select instance
          </Button>
        )}
      </div>
    );
  }
  return (
    <Badge
      variant="outline"
      className="gap-1.5 font-normal"
      style={instance.color ? { borderColor: instance.color, color: instance.color } : undefined}
    >
      <span
        className="h-1.5 w-1.5 rounded-full"
        style={{ backgroundColor: instance.color ?? "var(--primary)" }}
      />
      {instance.name}
      <span className="text-muted-foreground">· {instance.envLabel}</span>
    </Badge>
  );
}

function TenantSelector() {
  const instance = useAuth((s) => s.instance);
  const role = useAuth((s) => s.user?.role ?? null);
  const tenantScope = useAuth((s) => s.tenantScope);
  const sessionTenantName = useAuth((s) => s.tenantName);
  const location = useLocation();
  const navigate = useNavigate();
  const activeTenantId = location.pathname.match(/^\/tenants\/([^/]+)/)?.[1];
  const tenantQ = useQuery({
    queryKey: ["tenants", "selector"],
    queryFn: () => tenantApi.list(),
    enabled: Boolean(instance) && role === "super_admin",
  });
  const scopedTenantQ = useQuery({
    queryKey: ["tenant", tenantScope],
    queryFn: () => tenantApi.get(tenantScope!),
    enabled: Boolean(instance) && role !== "super_admin" && Boolean(tenantScope) && tenantScope !== "*",
  });
  const tenants: Tenant[] = tenantQ.data?.business ?? [];

  if (!instance) return null;

  if (role !== "super_admin") {
    if (!tenantScope || tenantScope === "*") return null;
    const tenantName = scopedTenantQ.data?.name ?? sessionTenantName ?? `Tenant ${tenantScope}`;
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-8 gap-1.5 font-normal"
        onClick={() => navigate(tenantBasePath(tenantScope))}
      >
        <Building2 className="h-3.5 w-3.5" />
        {tenantName}
      </Button>
    );
  }

  return (
    <select
      className="h-8 min-w-56 rounded-md border border-input bg-background px-3 text-sm"
      value={activeTenantId ? decodeURIComponent(activeTenantId) : ""}
      onChange={(e) => {
        const nextTenantId = e.target.value;
        if (!nextTenantId) return;
        const suffix = location.pathname.match(/^\/tenants\/[^/]+(\/.*)$/)?.[1] ?? "";
        navigate(`${tenantBasePath(nextTenantId)}${suffix}`);
      }}
      disabled={tenantQ.isLoading}
    >
      <option value="">{tenantQ.isLoading ? "Loading tenants…" : "Select tenant workspace…"}</option>
      {tenants.map((tenant) => (
        <option key={tenant.id} value={tenant.id}>{tenant.name}</option>
      ))}
    </select>
  );
}

function UserMenu() {
  const user = useAuth((s) => s.user);
  const logout = useAuth((s) => s.logout);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  if (!user) return null;
  const initials = user.username.slice(0, 2).toUpperCase();

  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="sm"
        className="gap-2"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-medium">
          {initials}
        </span>
        <span className="text-sm">{user.username}</span>
        <ChevronDown className="h-3.5 w-3.5 opacity-60" />
      </Button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-20 w-48 rounded-md border bg-popover p-1 shadow-md">
            <button
              className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
              onClick={() => {
                setOpen(false);
                navigate("/change-password");
              }}
            >
              Change password
            </button>
            <button
              className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-destructive hover:bg-accent"
              onClick={async () => {
                await logout();
                navigate("/login");
              }}
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function ThemeToggle() {
  const theme = useUi((s) => s.theme);
  const setTheme = useUi((s) => s.setTheme);
  const next = theme === "light" ? "dark" : theme === "dark" ? "system" : "light";
  const Icon = theme === "light" ? Sun : theme === "dark" ? Moon : Monitor;
  return (
    <Button
      variant="ghost"
      size="icon"
      className="h-8 w-8"
      title={`Theme: ${theme} (click → ${next})`}
      onClick={() => {
        setTheme(next);
        applyTheme(next);
      }}
    >
      <Icon className="h-4 w-4" />
    </Button>
  );
}

export function AppLayout({ children }: { children: ReactNode }) {
  const location = useLocation();
  const role = useAuth((s) => s.user?.role ?? null);
  const tenantScope = useAuth((s) => s.tenantScope);
  const pathTenantId = location.pathname.match(/^\/tenants\/([^/]+)/)?.[1];
  const tenantId = role === "super_admin" ? pathTenantId ? decodeURIComponent(pathTenantId) : null : tenantScope && tenantScope !== "*" ? tenantScope : null;
  const basePath = tenantId ? tenantBasePath(tenantId) : null;

  if (role !== "super_admin" && pathTenantId && tenantScope && tenantScope !== "*") {
    const requestedTenantId = decodeURIComponent(pathTenantId);
    if (requestedTenantId !== tenantScope) {
      const suffix = location.pathname.match(/^\/tenants\/[^/]+(\/.*)$/)?.[1] ?? "";
      return <Navigate to={`${tenantBasePath(tenantScope)}${suffix}`} replace />;
    }
  }

  return (
    <div className="flex min-h-screen flex-col">
      <header className="h-14 border-b flex items-center px-6 gap-6 bg-background sticky top-0 z-30">
        <div className="flex items-center gap-2.5">
          <Logo className="text-primary h-7 w-[28px]" />
          <span className="font-semibold text-sm tracking-tight">ServisSearch</span>
        </div>
        <InstanceBadge />
        <TenantSelector />
        <div className="ml-auto flex items-center gap-2">
          <ThemeToggle />
          <UserMenu />
        </div>
      </header>

      <div className="flex flex-1">
        <nav className="w-60 border-r bg-muted/40 p-3 flex flex-col gap-4 sticky top-14 h-[calc(100vh-3.5rem)] overflow-y-auto">
          <NavSection title="Administration" items={adminNav} />
          {basePath && (
            <>
              <NavSection title="Tenant Workspace" items={tenantNav(basePath, canManageTenant(role))} />
              <NavSection title="Interaction" items={interactionNav(basePath)} />
            </>
          )}
          {!basePath && (
            <div className="px-3 py-2 text-xs text-muted-foreground">
              {role === "super_admin" ? "Open a tenant to manage tenant resources." : roleLabel(role)}
            </div>
          )}
        </nav>

        <main className="flex-1 min-w-0 max-w-7xl mx-auto w-full px-6 py-6">
          {children}
        </main>
      </div>
    </div>
  );
}
